import md5 from 'md5'
import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import Image from 'shared/components/Image'
import PaymentType from 'shared/enums/PaymentType'
import getShortDisplay from 'account/utils/getShortDisplay'
import { AccountQA } from 'shared/dataAttributes'
import { Button } from 'shared/components/Button'
import { CreditCard } from 'account/models/BillingInfo'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { HistoricalOrder } from 'shared/types/HistoricalOrder'
import { IError } from 'errors/types/IError'
import { Order } from 'shared/types/Order'
import { OrderIssuesPanel } from 'account/components/OrderIssuesPanel'
import { PriceBreakdownLineItemType } from 'shared/enums/PriceBreakdownLineItemType'
import { colors, fonts, media } from 'shared/lib'
import { t } from 'localization'
import { useAccountPageContext } from 'account/components/AccountPageContextProvider'
import { useAuth } from 'shared/hooks/useAuth'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useRequestOrderTransferMutation } from 'api/ordersApi'
import { useUser } from 'shared/hooks/useUser'

const TP = 'account.components.orderSummaryBreakdownCard'

type OrderOrHistorical = Order | HistoricalOrder
type OrderSummaryBreakdownCardProps = {
  hashedEmail?: string
  isLoggedInWithSameEmailAsOrder?: boolean
  order: OrderOrHistorical
  setErrors?: (errors: IError[]) => void
  setIsLoading?: (input: boolean) => void
}

export const OrderSummaryBreakdownCard = ({
  hashedEmail,
  isLoggedInWithSameEmailAsOrder,
  order,
  setErrors = () => null,
  setIsLoading = () => null,
}: OrderSummaryBreakdownCardProps) => {
  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const isOrderCancellationEnabled = useFeatureFlag(FeatureFlag.WEB_FC_ORDER_CANCELLATION)
  const isMoreOrderInfoEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_MORE_ORDER_INFO)

  const { currentUser, isAuthenticated } = useUser()
  const { logoutUser } = useAuth()
  const { asPath, locale, push, replace } = useRouter()
  const [requestOrderTransfer, { isSuccess }] = useRequestOrderTransferMutation()
  const { activeOrderNumber, setActiveOrderNumber } = useAccountPageContext()
  const [displayOrderIssuesPanel, setDisplayOrderIssuePanel] = useState(false)
  const {
    cardBrand,
    last4Digits,
    number: orderNumber,
    paymentType,
    purchasedAt,
    status,
    buyerTitle,
  } = order

  useEffect(() => {
    if (isSuccess) {
      const queryParams = orderNumber ? `?active-order=${orderNumber}` : ''
      replace(`/my-account${queryParams}`, `/my-account${queryParams}`, { locale })
    }
    // replace dep changes on each render trigering infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, locale, orderNumber])

  // Get data from either HistoricalOrder or Order
  const details = isHistoricalOrder(order)
    ? order.item.productTemplate?.details
    : order.product.productTemplate?.details
  const { name } = isHistoricalOrder(order)
    ? order.item.productTemplate
    : order.product.productTemplate
  const { pictureUrl } = isHistoricalOrder(order)
    ? order.item.productTemplate
    : order.product.productTemplate
  const presentation = isHistoricalOrder(order)
    ? order.item.sizeOption?.presentation
    : order.product.sizeOption?.presentation
  const trackingNumber = isHistoricalOrder(order)
    ? order.fcTrackingNumber
    : order.trackingToBuyerCode
  const trackingUrl = isHistoricalOrder(order) ? order.fcTrackingUrl : order.trackingToBuyerCodeUrl
  const paymentTypeDisplay = getPaymentTypeDisplay({ paymentType, cardBrand, last4Digits })

  const handleLinkOrderToFullAccount = async () => {
    setIsLoading(true)
    setErrors([])
    try {
      await requestOrderTransfer(orderNumber)
    } catch (error) {
      setErrors([error as IError])
    } finally {
      setIsLoading(false)
    }
  }

  const handleResolveIssueClick = async () => {
    setIsLoading(true)
    if (!isGuestCheckoutEnabled || !asPath.includes('/order-status')) {
      setActiveOrderNumber(orderNumber || null)
      setIsLoading(false)
      return
    }

    // everything below only pertains if guest checkout FF enabled AND path is `/order-status`
    if (isAuthenticated && !currentUser?.isGuest && hashedEmail !== md5(currentUser?.email || '')) {
      // logged in with email different from guest order email

      // first log out user
      await logoutUser()

      // redirect to manage order page
      setIsLoading(false)
      push(
        `/manage-order/${orderNumber}?q=${hashedEmail}&requiresLogin=true`,
        `/manage-order/${orderNumber}?q=${hashedEmail}&requiresLogin=true`,
        { locale },
      )
      return
    }
    if (isLoggedInWithSameEmailAsOrder) {
      // logged in with same email as guest order email
      setIsLoading(false)
      return handleLinkOrderToFullAccount()
    }
    if (!isAuthenticated || currentUser?.isGuest) {
      setIsLoading(false)
      push(
        `/manage-order/${orderNumber}?q=${hashedEmail}`,
        `/manage-order/${orderNumber}?q=${hashedEmail}`,
        { locale },
      )
    }
  }

  const isOrderPanelDisplayed = activeOrderNumber === orderNumber || displayOrderIssuesPanel

  return (
    <Card>
      <Header>
        <div data-qa={AccountQA.OrderNumber}>
          {t(`${TP}.order`, 'Order #{orderNumber}', { orderNumber })}
        </div>
        <div data-qa={AccountQA.OrderDate}>{getShortDisplay(purchasedAt)}</div>
      </Header>
      <BodyContainer>
        <OrderDetails>
          <ProductSummary>
            <ImageContainer>
              <Image
                alt={name!}
                data-qa={AccountQA.OrderShoeThumbnail}
                sizes="150"
                src={pictureUrl!}
                width={104}
                height={74}
              />
            </ImageContainer>
            <ProductInformation>
              <ProductName data-testid="product-name" data-qa={AccountQA.OrderShoeName}>
                {name}
              </ProductName>
              {presentation && (
                <SizeDetail>
                  <DetailSize data-qa={AccountQA.OrderShoeSizeTag}>
                    {`${t(`${TP}.usSize`, 'US Size')} `}
                  </DetailSize>
                  <DetailSize data-testid="product-size" data-qa={AccountQA.OrderShoeSizeValue}>
                    {presentation}
                  </DetailSize>
                </SizeDetail>
              )}
              <div data-qa={AccountQA.OrderShoeDetails}>{details}</div>
            </ProductInformation>
          </ProductSummary>
          <PriceBreakdown>
            <LineItem>
              <LineItemText data-qa={AccountQA.OrderPaymentMethodTag} $accent={true}>
                {t(`${TP}.paymentMethod`, 'Payment Method')}
              </LineItemText>
              <LineItemValue data-qa={AccountQA.OrderPaymentMethodValue} $uppercase={true}>
                {paymentTypeDisplay}
              </LineItemValue>
            </LineItem>
            <BreakdownDetails>
              {order?.pricingBreakdown?.lineItems?.map((lineItem) => {
                if (
                  lineItem.type === PriceBreakdownLineItemType.APPLICABLE_COSTS &&
                  lineItem?.localizedAmount?.amount === 0
                ) {
                  return null
                } else {
                  return (
                    <LineItem key={lineItem?.type}>
                      <LineItemText data-qa={getDataQaLabel(lineItem?.type)}>
                        {lineItem?.label}
                      </LineItemText>
                      <LineItemValue data-qa={getDataQaAmount(lineItem?.type)}>
                        {lineItem?.formattedAmount}
                      </LineItemValue>
                    </LineItem>
                  )
                }
              })}
            </BreakdownDetails>
            <LineItem>
              <LineItemText data-qa={AccountQA.OrderTotalTag} $accent={true}>
                {order?.pricingBreakdown?.total?.label}
              </LineItemText>
              <LineItemValue
                data-qa={AccountQA.OrderTotalAmount}
                $bold={true}
                style={{ fontSize: '14px' }}
              >
                {order?.pricingBreakdown?.total?.formattedAmount}
              </LineItemValue>
            </LineItem>
          </PriceBreakdown>
          <ProductShipping>
            {buyerTitle && (
              <LineItem>
                <LineItemText data-qa={AccountQA.OrderShippingStatusTag}>
                  {t(`${TP}.orderStatus`, 'Order Status')}
                </LineItemText>
                <LineItemValue
                  data-testid="order-status"
                  data-qa={AccountQA.OrderShippingStatusValue}
                >
                  {isMoreOrderInfoEnabled ? (
                    <OrderStatusText
                      onClick={() => {
                        // From account page context to set active order number (i.e. My Orders)
                        setActiveOrderNumber(orderNumber)
                        // This handles local state to display panel
                        setDisplayOrderIssuePanel(true)
                      }}
                    >
                      {buyerTitle}
                    </OrderStatusText>
                  ) : (
                    <div>{buyerTitle}</div>
                  )}
                </LineItemValue>
              </LineItem>
            )}
            {trackingNumber && (
              <LineItem>
                <LineItemText>{t(`${TP}.trackingNumber`, 'Tracking Number')}</LineItemText>
                <LineItemValue
                  data-testid="tracking-number"
                  data-qa={AccountQA.OrderTrackingNumber}
                >
                  {trackingNumber}
                </LineItemValue>
              </LineItem>
            )}
            {trackingUrl && (
              <ButtonWrapper>
                <a rel="noopener noreferrer" target="_blank" href={trackingUrl}>
                  <TrackPackageButton $fill>
                    {t(`${TP}.trackPackage`, 'Track Package')}
                  </TrackPackageButton>
                </a>
              </ButtonWrapper>
            )}
            {isOrderCancellationEnabled &&
              buyerCancellableStatuses.has(status) &&
              isLessThan3Hours(purchasedAt) && (
                <ButtonWrapper>
                  <CancelOrderButton
                    onClick={handleResolveIssueClick}
                    data-qa={AccountQA.CancelOrderButton}
                  >
                    {t(`${TP}.cancelOrder`, 'Cancel Order')}
                  </CancelOrderButton>
                </ButtonWrapper>
              )}
            {status === 'goat_issue' && (
              <ButtonWrapper>
                <ResolveIssueButton
                  data-testid="resolve-issue-button"
                  onClick={handleResolveIssueClick}
                >
                  {isLoggedInWithSameEmailAsOrder
                    ? t(`${TP}.linkOrder`, 'Link Order')
                    : t(`${TP}.viewIssue`, 'View Issue')}
                </ResolveIssueButton>
              </ButtonWrapper>
            )}
          </ProductShipping>
        </OrderDetails>
        {isOrderPanelDisplayed && (
          <OrderIssuesPanel
            data-testid="order-issues-panel"
            isHeightFitContent={true}
            isVisible={isOrderPanelDisplayed}
            onClose={() => {
              setActiveOrderNumber(null)
              setDisplayOrderIssuePanel(false)
            }}
            orderNumber={orderNumber}
            paymentTypeDisplay={paymentTypeDisplay}
          />
        )}
      </BodyContainer>
    </Card>
  )
}

// UTILS
const buyerCancellableStatuses = new Set([
  'awaiting_fc_review',
  'awaiting_payment',
  'canceled_by_seller_review',
  'goat_review',
  'goat_review_risk_rejected',
  'goat_review_skipped',
  'sold',
])

const getDataQaAmount = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.CREDIT:
      return AccountQA.OrderFlightClubCredit
    case PriceBreakdownLineItemType.SHIPPING:
    case PriceBreakdownLineItemType.SUBTOTAL:
    case PriceBreakdownLineItemType.TAX:
      return AccountQA.OrderShoePrice
    default:
      return ''
  }
}

const getDataQaLabel = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.SHIPPING:
    case PriceBreakdownLineItemType.SUBTOTAL:
    case PriceBreakdownLineItemType.TAX:
      return AccountQA.OrderPaymentMethodTag
    default:
      return ''
  }
}

const getPaymentTypeDisplay = (payment: CreditCard) => {
  switch (payment.paymentType) {
    case PaymentType.CreditCard:
      return `${payment?.cardBrand} *${payment?.last4Digits}`
    case PaymentType.Alipay:
      return 'Alipay'
    case PaymentType.Affirm:
      return 'Affirm'
    case PaymentType.AfterPay:
      return 'AfterPay'
    case PaymentType.PayPal:
    case PaymentType.PayPalv2:
      return 'PayPal'
    case PaymentType.ApplePay:
      return 'Apple Pay'
    case PaymentType.AndroidPay:
      return 'Android Pay'
    case PaymentType.Stripe:
      return 'Stripe'
    case PaymentType.Klarna:
      return 'Klarna'
    default:
      return ''
  }
}

const isHistoricalOrder = (oh: OrderOrHistorical): oh is HistoricalOrder =>
  !!(oh as HistoricalOrder).item

const isLessThan3Hours = (time: string) => {
  if (!time) return false

  const now = new Date()
  const then = new Date(time)
  return now.getTime() - then.getTime() < 3 * 60 * 60 * 1000
}

const Card = styled.li`
  margin: 20px 0;
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
`
const DetailSize = styled.span`
  margin: 8px 3px 8px 0;
`
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  ${fonts.SUBTITLE_3};
  background-color: ${colors.FC2_LIGHTEST_GREY};
`
const BodyContainer = styled.div`
  margin: 50px 20px 30px 20px;
`
const ImageContainer = styled.div`
  max-width: 104px;
  margin-right: 12px;
  ${media.large`
  margin-right: 40px;
  `}
  height: 74px;
`
const BreakdownDetails = styled.div`
  margin: 16px 0;
  width: 100%;
  ${media.medium`
    margin: 4px 0;
  `}
`
const LineItem = styled.div`
  align-items: start;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  ${media.medium`
    margin-bottom: 8px;
  `}
  width: 100%;
`
const LineItemText = styled.div<{ $accent?: boolean }>`
  ${({ $accent }) => ($accent ? fonts.SUBTITLE_3 : fonts.BODY_TEXT)}
  color: ${colors.FC2_GREY};
  padding-right: 10px;
`
const LineItemValue = styled.div<{ $bold?: boolean; $uppercase?: boolean }>`
  ${fonts.SUBTITLE_3};
  ${({ $bold }) => ($bold ? 'font-weight: bold;' : '')}
  ${({ $uppercase }) => $uppercase && 'text-transform: uppercase;'}
  text-align: right;
  ${media.large`
    width: auto;
  `}
`
const PriceBreakdown = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  ${media.medium`
    flex-basis: 30%;
    margin: 0 20px 0 0;
    max-width: 200px;
  `}
`
const OrderDetails = styled.div`
  flex-direction: column;
  ${media.medium`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `}
`
const OrderStatusText = styled.div`
  cursor: pointer;
  text-decoration: underline;
`
const ProductInformation = styled.div`
  flex-grow: 1;
  ${fonts.BODY_TEXT}
`
const ProductName = styled.div`
  ${fonts.SUBTITLE_2}
  font-weight: bold;
  margin-bottom: 5px;
`
const ProductSummary = styled.div`
  display: flex;
  margin: 0 0 24px 0;
  ${media.medium`
    margin: 0 20px 0 0;
    flex-basis: 40%;
  `}
`
const ProductShipping = styled.div`
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  margin-top: 34px;
  padding-top: 34px;
  ${media.medium`
    border-top: none;
    flex-basis: 30%;
    margin: 0;
    max-width: 280px;
    padding-top: 0;
  `}
`
const SizeDetail = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  display: flex;
`
const ButtonWrapper = styled.div`
  text-align: right;
  margin-bottom: 8px;
`
const TrackPackageButton = styled(Button).attrs({
  buttonType: 'secondary',
})`
  ${media.large`
    width: 150px;
    height: 30px;
    line-height: 30px;
    min-height: auto;
  `}
`
const CancelOrderButton = styled.button`
  margin-top: 36px;
  text-align: right;
  font-weight: 500;
  color: ${colors.FC2_RED};
`
const ResolveIssueButton = styled(Button).attrs({
  buttonType: 'primary2',
})`
  width: 100%;
  ${media.large`
    width: 150px;
    height: 30px;
    line-height: 30px;
    min-height: auto;
  `}
`
